import type { EventBus } from "../core/eventBus.ts";
import type { PointerInputEvent } from "../core/types.ts";

export type AppMode = "debug" | "play";

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Minimum gap between drag samples. Roughly one frame at 60Hz. */
const MOVE_INTERVAL_MS = 15;

/**
 * The thumb channel — the moment-to-moment verb (design doc §2, §3).
 *
 * Blinking cannot carry this load. A healthy rate is one blink every 3–4
 * seconds, so any mechanic demanding them faster fights physiology, exhausts
 * the player inside thirty seconds, and trains exactly the pattern the product
 * exists to undo. Blink is the low-frequency, high-impact verb; the thumb is
 * the fast one.
 *
 * The same full-screen surface serves both modes, which is why it exists at
 * all: seven consecutive real sessions came back with zero labels because the
 * label control was a button among many. A target that fills the screen cannot
 * be missed, and its meaning is set by mode — label while recording, act while
 * playing.
 */
export class PointerChannel {
  #detach: (() => void) | null = null;

  constructor(
    private readonly bus: EventBus,
    private readonly surface: HTMLElement,
    /** Frame time for the event, so input shares the signal's clock. */
    private readonly now: () => number,
  ) {}

  /**
   * True for anything the page needs to keep working as a control.
   *
   * The listener sits on the document so a press anywhere counts — the video,
   * the panel, the margins, the chart. That is deliberate: a target confined to
   * the video is a target you can miss, and missing it is exactly how seven
   * consecutive sessions came back with nothing recorded. But "anywhere" cannot
   * include the buttons, or starting the camera would also register a jump.
   */
  #isControl(target: EventTarget | null): boolean {
    return (
      target instanceof Element &&
      // `summary` and not `details`: opening the accordion must not fire the
      // verb, but a press on the copy inside it is still a press on the page.
      target.closest("button, select, input, textarea, a, label.field, summary") !== null &&
      // The full-screen surface is itself a button, and it IS the target.
      target.closest(".tap-surface") === null
    );
  }

  attach(): void {
    if (this.#detach) return;

    const emit = (phase: PointerInputEvent["phase"]) => (e: PointerEvent) => {
      if (this.#isControl(e.target)) return;

      const rect = this.surface.getBoundingClientRect();
      const hasRect = rect.width > 0 && rect.height > 0;
      this.bus.emit({
        type: "pointer",
        t: this.now(),
        phase,
        // Normalised against the play surface, so a replay is
        // resolution-agnostic. A press outside it still counts; it just clamps.
        x: hasRect ? clamp01((e.clientX - rect.left) / rect.width) : 0.5,
        y: hasRect ? clamp01((e.clientY - rect.top) / rect.height) : 0.5,
      });
    };

    const emitDown = emit("down");
    const emitUp = emit("up");
    const emitMove = emit("move");

    // Drag state. `move` is only meaningful between a down and its up, and
    // emitting on hover would put mouse noise into every desktop recording.
    let held = false;
    let lastMoveAt = 0;

    const down = (e: PointerEvent) => {
      held = true;
      lastMoveAt = 0;
      emitDown(e);
    };
    const up = (e: PointerEvent) => {
      if (!held) return;
      held = false;
      emitUp(e);
    };
    const move = (e: PointerEvent) => {
      if (!held) return;
      // Throttled to roughly a frame. Browsers coalesce pointermove already,
      // but not on every platform, and a 240Hz digitiser would otherwise put
      // four events per frame into the recording for no added fidelity.
      const now = this.now();
      if (now - lastMoveAt < MOVE_INTERVAL_MS) return;
      lastMoveAt = now;
      emitMove(e);
    };

    const root = this.surface.ownerDocument;
    root.addEventListener("pointerdown", down);
    root.addEventListener("pointerup", up);
    root.addEventListener("pointermove", move);
    // Losing the pointer must still release, or a held press sticks forever and
    // the mechanic reads as jammed.
    root.addEventListener("pointercancel", up);

    this.#detach = () => {
      root.removeEventListener("pointerdown", down);
      root.removeEventListener("pointerup", up);
      root.removeEventListener("pointermove", move);
      root.removeEventListener("pointercancel", up);
    };
  }

  detach(): void {
    this.#detach?.();
    this.#detach = null;
  }
}
